import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import {
  Category,
  getCategoriesForProvider,
  getRefProvider,
  getInitialCategory,
  getInitialStoreName,
  getInitialThemeKey,
} from 'config';

interface AppState {
  msId: string | null;
  sessionId: string | null;
  selectedCategory: Category | null;
  selectedThemeKey: string | null;
  currentStepId: string | null;
  storeNameSuggestions: string[];
  selectedStoreName: string | null;
  paymentsEnabled: boolean;
  pendingRedirect: {
    returnToStepId: string;
    timestamp: number;
  } | null;
}

interface AppActions {
  setMsId: (msId: string) => void;
  setSessionId: (sessionId: string) => void;
  setSelectedCategory: (category: Category | null) => void;
  resetSelectedCategory: () => void;
  setSelectedThemeKey: (themeKey: string | null) => void;
  resetSelectedThemeKey: () => void;
  setCurrentStepId: (stepId: string | null) => void;
  setStoreNameSuggestions: (suggestions: string[]) => void;
  setSelectedStoreName: (storeName: string | null) => void;
  setPaymentsEnabled: (enabled: boolean) => void;
  setPendingRedirect: (redirect: AppState['pendingRedirect']) => void;
  clearPendingRedirect: () => void;
  resetStore: () => void;
}

type Store = AppState & AppActions;

const getInitialState = (): AppState => {
  const supported = getCategoriesForProvider(getRefProvider());
  const initial = getInitialCategory();

  const selectedCategory = initial && supported.includes(initial) ? initial : null;

  return {
    msId: null,
    sessionId: null,
    selectedCategory,
    selectedThemeKey: getInitialThemeKey() ?? null,
    currentStepId: null,
    storeNameSuggestions: [],
    selectedStoreName: getInitialStoreName() ?? null,
    paymentsEnabled: true,
    pendingRedirect: null,
  };
};

export const SESSION_STORAGE_KEY = 'ai-store-builder-session';

export const useAppStore = create<Store>()(
  devtools(
    persist(
      (set) => ({
        ...getInitialState(),

        setMsId: (msId) => set({ msId }),
        setSessionId: (sessionId) => set({ sessionId }),
        setSelectedCategory: (selectedCategory) => set({ selectedCategory }),
        resetSelectedCategory: () => set({ selectedCategory: null }),
        setSelectedThemeKey: (selectedThemeKey) => set({ selectedThemeKey }),
        resetSelectedThemeKey: () => set({ selectedThemeKey: null }),
        setCurrentStepId: (currentStepId) => set({ currentStepId }),
        setStoreNameSuggestions: (storeNameSuggestions) => set({ storeNameSuggestions }),
        setSelectedStoreName: (selectedStoreName) => set({ selectedStoreName }),
        setPaymentsEnabled: (paymentsEnabled) => set({ paymentsEnabled }),
        setPendingRedirect: (pendingRedirect) => set({ pendingRedirect }),
        clearPendingRedirect: () => set({ pendingRedirect: null }),
        resetStore: () => set(getInitialState()),
      }),
      {
        name: SESSION_STORAGE_KEY,
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          msId: state.msId,
          selectedCategory: state.selectedCategory,
          selectedThemeKey: state.selectedThemeKey,
          currentStepId: state.currentStepId,
          storeNameSuggestions: state.storeNameSuggestions,
          selectedStoreName: state.selectedStoreName,
          paymentsEnabled: state.paymentsEnabled,
          pendingRedirect: state.pendingRedirect,
        }),
      },
    ),
    { name: 'AppStore' },
  ),
);

// Selectors
export const useMsId = () => useAppStore((state) => state.msId);
export const useSelectedCategory = () => useAppStore((state) => state.selectedCategory);
export const useCurrentStepId = () => useAppStore((state) => state.currentStepId);
export const useStoreNameSuggestions = () => useAppStore((state) => state.storeNameSuggestions);
export const useSelectedStoreName = () => useAppStore((state) => state.selectedStoreName);
export const usePaymentsEnabled = () => useAppStore((state) => state.paymentsEnabled);
export const usePendingRedirect = () => useAppStore((state) => state.pendingRedirect);

// Action hooks
export const useSetMsId = () => useAppStore((state) => state.setMsId);
export const useSetSelectedCategory = () => useAppStore((state) => state.setSelectedCategory);
export const useResetSelectedCategory = () => useAppStore((state) => state.resetSelectedCategory);
export const useSetSelectedThemeKey = () => useAppStore((state) => state.setSelectedThemeKey);
export const useResetSelectedThemeKey = () => useAppStore((state) => state.resetSelectedThemeKey);
export const useSetCurrentStepId = () => useAppStore((state) => state.setCurrentStepId);
export const useSetStoreNameSuggestions = () => useAppStore((state) => state.setStoreNameSuggestions);
export const useSetSelectedStoreName = () => useAppStore((state) => state.setSelectedStoreName);
export const useSetPaymentsEnabled = () => useAppStore((state) => state.setPaymentsEnabled);
export const useSetPendingRedirect = () => useAppStore((state) => state.setPendingRedirect);
export const useClearPendingRedirect = () => useAppStore((state) => state.clearPendingRedirect);
export const useResetStore = () => useAppStore((state) => state.resetStore);
